/** @jsx isolateComponent */

import {
  ReactNode,
  useState,
  SyntheticEvent,
  MouseEvent,
  KeyboardEvent,
  FocusEvent,
  ChangeEvent,
} from 'react';
import { css } from '@emotion/core';
import { Tooltip } from '../../display/Tooltip';
import { createUniqueCustomProperties } from '../../../utils/createUniqueCustomProperty';
import { Animation, Colors, Shadows, Typography } from '../../../theme';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/style-encapsulation';
import { ShadowName } from '../../../theme/Shadows';
import { CssProp } from '../../../ts-types';
import { uniqueId } from '../../../utils/uniqueId';

type InputElement = HTMLInputElement | HTMLTextAreaElement;

export type InputEvent = SyntheticEvent<InputElement>;

type RenderIconProps = {
  // Only one size supported for now
  iconSize: 24;
  isFocused: boolean;
  isHovered: boolean;
};

export type Props = {
  /**
   * Identifies the currently active descendant of a composite component.
   * This is used when `TextInput` is part of a composite component,
   * such as `Typeahead`. Without this, some functionality doesn't
   * work well for screen readers.
   */
  ariaActiveDescendant?: string;

  /**
   * Identifies the element whose contents or presence are controlled by
   * this component.
   * This is used when `TextInput` is part of a composite component,
   * such as `Typeahead`. Without this, some functionality doesn't
   * work well for screen readers.
   */
  ariaControls?: string;

  /**
   * Accessibility label that describes this component. This should be supplied
   * if no corresponding `label` element is used.
   */
  ariaLabel?: string;

  /**
   * Accessibility label referencing the ID of an element which labels this
   * component.
   */
  ariaLabelledBy?: string;

  /**
   * Accessibility label referencing the ID of an element which describes this
   * component.
   */
  ariaDescribedBy?: string;

  /**
   * Indicates whether the input should be hidden from screenreaders.
   * Use when there is a separate control, like when the input is disabled.
   */
  ariaHidden?: boolean;

  /**
   * Accessibility label used to indicate that the value entered into an input field does not conform to the format expected by the application
   */
  ariaInvalid?: boolean;

  /**
   * True if user input is required before a form can be submitted.
   */
  ariaRequired?: boolean;

  /**
   * Whether the field can be autocompleted by the browser,
   * and if so what type of information should be autocompleted.
   * See https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
   */
  autoComplete?: string;

  /**
   * Whether the input should be focused when it's initially rendered.
   */
  autoFocus?: boolean;

  /**
   * Pass in a value to determine which side of the button should be rounded.
   * Good for when the input needs to be flush against a flat surface
   */
  borderRadiusSide?: 'left' | 'right';

  /**
   * Indicates whether the icon that renders on the left of the `Input`
   * can be clicked. Set to `false` if clicking it should pass through
   * to the underlying rendered `children`.
   */
  canClickIconBefore?: boolean;

  /**
   * Indicates whether the icon that renders on the right of the `Input`
   * can be clicked. Set to `false` if clicking it should pass through
   * to the underlying rendered `children`.
   */
  canClickIconAfter?: boolean;

  /**
   * Function to render the input itself.
   */
  children: (b: InputChildrenAttrs, a: DerivedState) => ReactNode;

  /**
   * Color theme of the input.
   */
  color: 'grey100' | 'white';

  /**
   * The text that renders in a `Tooltip` when the input `isDisabled`. Only
   * renders when `isDisabled`.
   *
   * If empty string or null, then no `Tooltip` is rendered when the input
   * `isDisabled`.
   */
  disabledText?: string;

  /**
   * The text that renders in a `Tooltip` when the input `isReadonly`. Only
   * renders when `isDisabled`.
   *
   * If empty string or null, then no `Tooltip` is rendered when the input
   * `isReadonly`.
   */
  readOnlyText?: string;

  /**
   * HTML `id` attribute for the element, used to associate it with a `label`.
   */
  id?: string;

  /**
   * Disables the input and makes it non-interactive.
   */
  isDisabled?: boolean;

  /**
   * Is the input full width?
   * @default false
   */
  isFullWidth?: boolean;

  /**
   * Indicates whether the field has an error.
   */
  isErrored?: boolean;

  /**
   * Whether the input can only be read from. Contrast this with
   * `isDisabled`, which completely disables the input, while
   * `isReadOnly` leaves the input functional.
   */
  isReadonly?: boolean;

  /**
   * Whether the input can be focused or not
   * @default true
   */
  isFocusable?: boolean;

  /**
   * Indicates whether the field is required.
   */
  isRequired?: boolean;

  /** Optional max length for text inputs */
  maxLength?: number;

  /**
   * HTML name attribute, typically used for the key that will
   * be sent to the API.
   */
  name?: string;

  /**
   * Callback executed when the input is blurred.
   */
  onBlur?: (event: FocusEvent<HTMLElement>, meta?: any) => void;

  /**
   * Callback executed when the input's value is changed.
   */
  onChange?: React.ChangeEventHandler;

  /**
   * Callback executed when the input is clicked.
   */
  onClick?: (event?: MouseEvent) => void;

  /**
   * Callback executed when the input is focused.
   */
  onFocus?: (event: FocusEvent<HTMLElement>) => void;

  /**
   * Callback executed when a key is pressed and the input is focused.
   */
  onKeyDown?: (event: KeyboardEvent<HTMLElement>) => void;

  /**
   * A hint to the user of what can be entered in the control.
   * **Note that this is not a sufficient label for accessibility!**
   * Be sure to either associate this input with a `label` element
   * or use the `ariaLabel` prop!
   */
  placeholder?: string;

  /**
   * Function that optionally returns an icon to render
   * on the left side of the input.
   */
  renderIconBefore?: (options: RenderIconProps) => ReactNode | undefined | null;

  /**
   * Function that optionally returns an icon to render
   * on the right side of the input.
   */
  renderIconAfter?: (options: RenderIconProps) => ReactNode | undefined | null;

  /**
   * Indicates role for the field, typically empty.
   */
  role?: string;

  /**
   * Indicates the type of shadow the input has.
   */
  shadow?: ShadowName;

  /**
   * The rendered size of the input.
   */
  size: 'small' | 'medium';

  /**
   * The actual value of the input.
   */
  value?: string;
};

export type InputChildrenAttrs = {
  'aria-activedescendant'?: string;
  'aria-controls'?: string;
  'aria-describedby'?: string;
  'aria-hidden'?: boolean;
  'aria-invalid'?: boolean;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-required'?: boolean;
  tabIndex?: number;
  autoComplete?: string;
  autoFocus?: boolean;
  css?: CssProp;
  disabled?: boolean;
  id?: string;
  maxLength?: number;
  name?: string;
  onBlur?: (event: FocusEvent<HTMLElement>) => void;
  onChange?: (event: ChangeEvent<HTMLElement>) => void;
  onFocus?: (event: FocusEvent<HTMLElement>) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLElement>) => void;
  placeholder?: string;
  readOnly?: boolean;
  required?: boolean;
  role?: string;
  value?: string;
};

type DerivedState = {
  hasIconBefore: boolean;
  hasIconAfter: boolean;
  isFocused: boolean;
  isHovered: boolean;
};

const sizeToHeight = {
  small: 40,
  medium: 50,
} as const;

// Only one size supported for now.
const sizeToIconSize = {
  small: 24,
  medium: 24,
} as const;

// Unfortunately placeholders are kind of tricky to style
// and these selectors can't be comma separated.
const stylePlaceholder = (color: string) => ({
  '&::placeholder': {
    color,
    // Necessary for Safari
    // '-webkit-text-fill-color': color,
    WebkitTextFillColor: color,
  },
  '&::-moz-placeholder': {
    // Firefox has <1 `opacity` on its placeholders,
    // so we hafta override it.
    opacity: 1,
    color,
  },
});

const InputColors = {
  grey100: {
    backgroundColor: Colors.grey100,
  },
  white: {
    backgroundColor: Colors.white,
  },
};

const Vars = createUniqueCustomProperties('Input', ['tooltipOpacity']);

const Styles = labelStyles('Input', {
  root: (props: Props) => [
    css`
      display: grid;
      grid-template-areas: 'iconBefore input iconAfter';
      /* For Tooltip positioning */
      position: relative;
      /* Defined here so box-shadow behaves as expected. */
      border-radius: 10px;
      ${Vars.tooltipOpacity}: 0;

      &[data-disabled] {
        cursor: not-allowed;
      }

      &[data-readonly]:hover,
      &[data-disabled]:hover {
        ${Vars.tooltipOpacity}: 1;
      }
    `,
    withModifier(
      props.size,
      css({
        gridTemplateColumns: `${sizeToHeight[props.size]}px 1fr ${
          sizeToHeight[props.size]
        }px`,
      })
    ),
    props.shadow &&
      withModifier(props.shadow, {
        boxShadow: Shadows[props.shadow],
      }),
    props.borderRadiusSide &&
      withModifier(`borderRadiusSide-${props.borderRadiusSide}`, {
        borderRadius: {
          all: 10,
          left: '10px 0 0 10px',
          right: '0 10px 10px 0',
        }[props.borderRadiusSide],
      }),
    props.isFullWidth &&
      withModifier('isFullWidth', {
        width: '100%',
        maxWidth: '100%',
      }),
  ],
  input: (props: Props, derivedState: DerivedState) => [
    {
      ...Typography.smallPrint1Semibold,
      fontWeight: 600,
      backgroundColor: Colors.gray10,
      color: Colors.gray80,
      // Defined on `root` so `root`'s `boxShadow` behaves as expected.
      // This node uses `boxShadow` for its focus state.
      borderRadius: 'inherit',
      // Stretch all the way across. `-1` would be a better
      // value than `4` but Safari doesn't like it.
      gridArea: '1 / 1 / 4 / 4',
      transitionDuration: Animation.transitionDuration,
      // Only transition specific properties because some,
      // like `padding`, will change but shouldn't transition.
      transitionProperty: 'background-color, color, box-shadow',
      transitionTimingFunction: 'ease',
      ...stylePlaceholder(Colors.gray60),
      // Bump font size for devices that don't support hovering
      // to prevent the fields from automatically zooming in on those
      // devices. 16px is defined at the OS level of these devices,
      // specifically iOS.
      '@media (hover: none)': {
        fontSize: 16,
      },
      // Hides additional "clear selection" X icon in Edge.
      // https://zappy.zapier.com/F693DF53-79B7-4857-AADE-73E9BF3011DB.png
      '&::-ms-clear': {
        display: 'none',
      },
      // Hides additional "clear selection" X icon in Safari.
      '&::-webkit-search-cancel-button': {
        display: 'none',
      },
    },
    withModifier(props.color, {
      backgroundColor: InputColors[props.color].backgroundColor,
    }),
    props.isErrored &&
      withModifier('errored', {
        boxShadow: `0 0 0 2px ${Colors.error}`,
      }),
    derivedState.isHovered &&
      props.isFocusable &&
      withModifier('hovered', {
        boxShadow: `0 0 0 2px ${Colors.gray20}`,
      }),
    derivedState.isFocused &&
      props.isFocusable &&
      withModifier('focused', {
        boxShadow: `0 0 0 2px ${Colors.ocean}`,
      }),
    props.isDisabled &&
      withModifier('disabled', {
        boxShadow: 'none',
        color: Colors.gray60,
        WebkitTextFillColor: Colors.gray60,
        whiteSpace: 'pre-wrap',
        cursor: 'not-allowed',
        ...stylePlaceholder(Colors.gray60),
      }),
    props.size === 'small' &&
      withModifier(props.size, {
        height: sizeToHeight[props.size],
        padding: '8px 15px',
      }),
    props.size === 'medium' &&
      withModifier(props.size, {
        // One of the rare departures from `Typography`.
        fontSize: 16,
        lineHeight: '25px',
        height: sizeToHeight[props.size],
        padding: '10px 20px',
      }),
    (derivedState.hasIconBefore || derivedState.hasIconAfter) &&
      withModifier('hasIcon', {
        paddingLeft: derivedState.hasIconBefore && sizeToHeight[props.size],
        paddingRight: derivedState.hasIconAfter && sizeToHeight[props.size],
      }),
    // Prevent input from "leaking" out of the container
    { minWidth: '100%' },
  ],
  icon: (
    props: Props,
    iconOpts: {
      position: 'before' | 'after';
    }
  ) => [
    css({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: Colors.gray60,
      position: 'relative',
      zIndex: 1,
    }),
    // Purely for Safari because it doesn't stretch grid
    // items correctly.
    withModifier(
      props.size,
      css({
        height: sizeToHeight[props.size],
      })
    ),
    withModifier(
      iconOpts.position,
      css({
        gridArea: iconOpts.position === 'after' ? 'iconAfter' : 'iconBefore',
      })
    ),
    ((!props.canClickIconBefore && iconOpts.position === 'before') ||
      (!props.canClickIconAfter && iconOpts.position === 'after')) &&
      withModifier(
        'disable-click',
        css({
          pointerEvents: 'none',
        })
      ),
  ],
  tooltip: css`
    opacity: var(${Vars.tooltipOpacity});
    transition: ${Animation.transitionValue};
  `,
});

/**
 * Renders a wrapper node that may render icons and tooltips. Within
 * that node `children` are rendered as a function which should return
 * some sort of input or input-like element.
 */
export const Input = (props: Props) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipId] = useState(
    props.id ? `${props.id}-tooltip` : uniqueId(`Input__tooltip-`)
  );
  const shouldRenderTooltip =
    (props.isDisabled && !!props.disabledText) ||
    (props.isReadonly && !!props.readOnlyText);

  const renderIconProps = {
    iconSize: sizeToIconSize[props.size],
    isFocused,
    isHovered,
  };

  const iconBefore =
    props.renderIconBefore && props.renderIconBefore(renderIconProps);

  const iconAfter =
    props.renderIconAfter && props.renderIconAfter(renderIconProps);

  const derivedState = {
    hasIconBefore: !!iconBefore,
    hasIconAfter: !!iconAfter,
    isFocused,
    isHovered,
  };

  return (
    <div
      css={Styles.root(props)}
      data-disabled={props.isDisabled ? '' : undefined}
      data-readonly={props.isReadonly ? '' : undefined}
      onBlur={() => setIsFocused(false)}
      onClick={props.onClick}
      onFocus={() => setIsFocused(true)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role="presentation"
    >
      {iconBefore && (
        <div
          css={Styles.icon(props, {
            position: 'before',
          })}
        >
          {iconBefore}
        </div>
      )}
      {props.children(
        {
          'aria-activedescendant': props.ariaActiveDescendant,
          'aria-controls': props.ariaControls,
          'aria-describedby': shouldRenderTooltip
            ? tooltipId
            : props.ariaDescribedBy,
          'aria-hidden': props.ariaHidden,
          'aria-invalid': props.isErrored,
          'aria-label': props.ariaLabel,
          'aria-labelledby': props.ariaLabelledBy,
          'aria-required': props.ariaRequired,
          autoComplete: props.autoComplete,
          autoFocus: props.autoFocus,
          css: Styles.input(props, derivedState),
          tabIndex: props.isFocusable ? undefined : -1,
          disabled: props.isDisabled,
          id: props.id,
          maxLength: props.maxLength,
          name: props.name,
          onBlur: props.onBlur,
          onChange: props.onChange,
          onFocus: props.onFocus,
          onKeyDown: props.onKeyDown,
          placeholder: props.placeholder,
          readOnly: props.isReadonly,
          required: props.isRequired,
          role: props.role,
          value: props.value,
        },
        derivedState
      )}
      {iconAfter && (
        <div
          css={Styles.icon(props, {
            position: 'after',
          })}
        >
          {iconAfter}
        </div>
      )}
      {shouldRenderTooltip && (
        <div css={Styles.tooltip}>
          <Tooltip id={tooltipId} position="south">
            {props.isReadonly ? props.readOnlyText : props.disabledText}
          </Tooltip>
        </div>
      )}
    </div>
  );
};

Input.defaultProps = {
  isFocusable: true,
  canClickIconBefore: true,
  canClickIconAfter: true,
  color: 'grey100',
  disabledText: 'Disabled',
  readOnlyText: 'Read-Only',
  size: 'small',
  isFullWidth: false,
};
